import React, { useId } from 'react';
import { twMerge } from 'utilities/src/twMerge';

import IconComponent from '../../Icon';
import CheckboxIcon from '../../../icons/DS/_Checkbox';
import RadioIcon from '../../../icons/DS/_Radio';

import { InputThemeEnum } from '../shared/utils/themes';
import { TBoxTypeEnum, TFormField } from '../shared/utils/types';

import { MessageError } from '../shared/components';

import type { ICheckboxProps } from './_props';
import useCheckboxTheme from './themes';

/**
 * Checkbox
 */

function Checkbox<TFormFields extends TFormField = TFormField>({
  name,
  label = '',
  legend = '',
  errorMessage = '',
  errorIcon = undefined,
  errorClassName = '',
  type = 'Checkbox',
  state = 'Default',
  theme = 'Light',
  required = false,
  checked,
  value = '',
  className = '',
  register,
  rules,
  onChange,
  onBlur,
  errorSpace = false,
}: ICheckboxProps<TFormFields>) {
  const id = useId();

  const {
    fieldsetClasses,
    inputClasses,
    inputThemeClasses,
    labelTextClasses,
    checkboxLabelClasses,
    boxClasses,
    iconClasses,
  } = useCheckboxTheme({ theme, state, type });
  const inputCheckedClasses = inputThemeClasses.get(TBoxTypeEnum[type]).get(InputThemeEnum[theme]);

  return (
    <div className={className}>
      <fieldset className={fieldsetClasses}>
        <legend className="hidden"> {legend} </legend>

        <input
          id={id}
          type={TBoxTypeEnum[type]}
          className={twMerge(inputClasses, inputCheckedClasses)}
          name={name}
          value={value}
          checked={checked}
          required={required}
          onChange={onChange}
          onBlur={onBlur}
          {...(register &&
            register(name, {
              onChange,
              onBlur,
              ...rules,
            }))}
        />

        <label htmlFor={id} className={checkboxLabelClasses}>
          <div className={boxClasses}>
            <IconComponent
              Icon={type === 'Checkbox' ? CheckboxIcon : RadioIcon}
              size="IconS"
              options={{
                className: iconClasses,
              }}
            />
          </div>

          <span className={labelTextClasses}> {label} </span>
        </label>
      </fieldset>

      <MessageError
        message={errorMessage}
        icon={errorIcon}
        state={state}
        theme={theme}
        className={twMerge('mt-1', errorClassName)}
        errorSpace={errorSpace}
      />
    </div>
  );
}

/**
 * Exports
 */

export { ICheckboxProps };

export default Checkbox;
